import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Button,
  Space,
  Table,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  message,
  Divider,
  Progress,
  Empty,
  Spin,
  Popconfirm
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  UndoOutlined,
  BankOutlined,
  ReconciliationOutlined,
  LinkOutlined,
  DisconnectOutlined,
  CheckCircleOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import bankReconciliationService, {
  BankAccount,
  BankStatement,
  BankStatementLine,
  ReconciliationSummary,
  MatchingCandidate,
  DashboardStats
} from '@/services/bankReconciliationService';
import journalService, { Journal } from '@/services/journalService';

const { Title, Text } = Typography;
const { Option } = Select;

const BankReconciliation: React.FC = () => {
  const [activeTab, setActiveTab] = useState('reconciliation');
  const [loading, setLoading] = useState(false);

  // Bank accounts
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  // Journals to choose from when linking a bank account. Matching looks ONLY
  // inside the linked journal, so an account without one can never match
  // anything — findMatchingJournalEntryLines returns [] the moment journalId is
  // null. The field was missing from this form, which is why reconciliation
  // showed "No matching candidates found" for every line.
  const [journals, setJournals] = useState<Journal[]>([]);
  const [editLineModalVisible, setEditLineModalVisible] = useState(false);
  const [editingLine, setEditingLine] = useState<BankStatementLine | null>(null);
  const [editStatementModalVisible, setEditStatementModalVisible] = useState(false);
  const [selectedBankAccountId, setSelectedBankAccountId] = useState<string | null>(null);
  const [bankAccountModalVisible, setBankAccountModalVisible] = useState(false);
  const [editingBankAccount, setEditingBankAccount] = useState<BankAccount | null>(null);
  const [bankAccountForm] = Form.useForm();

  // Statements
  const [statements, setStatements] = useState<BankStatement[]>([]);
  const [selectedStatement, setSelectedStatement] = useState<BankStatement | null>(null);
  const [reconciliationSummary, setReconciliationSummary] = useState<ReconciliationSummary | null>(null);
  const [statementModalVisible, setStatementModalVisible] = useState(false);
  const [statementForm] = Form.useForm();

  // Transaction line form
  const [addLineModalVisible, setAddLineModalVisible] = useState(false);
  const [addLineForm] = Form.useForm();
  const [editLineForm] = Form.useForm();
  const [editStatementForm] = Form.useForm();

  // Matching
  const [matchModalVisible, setMatchModalVisible] = useState(false);
  const [selectedLineForMatch, setSelectedLineForMatch] = useState<BankStatementLine | null>(null);
  const [matchingCandidates, setMatchingCandidates] = useState<MatchingCandidate[]>([]);
  const [matchingLoading, setMatchingLoading] = useState(false);

  // Dashboard stats
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);

  // Fetch bank accounts on mount
  useEffect(() => {
    fetchBankAccounts();
    fetchDashboardStats();
    fetchJournals();
  }, []);

  // Fetch statements when bank account changes
  useEffect(() => {
    if (selectedBankAccountId) {
      fetchStatements();
    } else {
      setStatements([]);
      setSelectedStatement(null);
    }
  }, [selectedBankAccountId]);

  // Fetch reconciliation summary when statement changes
  useEffect(() => {
    if (selectedStatement) {
      fetchReconciliationSummary();
    } else {
      setReconciliationSummary(null);
    }
  }, [selectedStatement?.id]);

  const fetchJournals = async () => {
    try {
      const data = await journalService.getActiveJournals();
      // Bank and cash journals first — those are what a bank account posts to —
      // but keep the rest, since some accounts are reconciled through a general
      // journal.
      const rank = (t?: string) => (t === 'BANK' ? 0 : t === 'CASH' ? 1 : 2);
      setJournals([...data].sort((a, b) => rank(a.type) - rank(b.type)
        || (a.code || '').localeCompare(b.code || '')));
    } catch (error) {
      console.error('Failed to load journals:', error);
      setJournals([]);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      setLoading(true);
      const data = await bankReconciliationService.getAllBankAccounts();
      setBankAccounts(data);
      // Auto-select first account if available
      if (data.length > 0 && !selectedBankAccountId) {
        setSelectedBankAccountId(data[0].id);
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch bank accounts');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatements = async () => {
    if (!selectedBankAccountId) return;

    try {
      setLoading(true);
      const data = await bankReconciliationService.getAllStatements({
        bankAccountId: selectedBankAccountId
      });
      setStatements(data);
      // Auto-select first open statement if available
      const openStatement = data.find(s => s.state === 'OPEN');
      if (openStatement) {
        setSelectedStatement(openStatement);
      } else if (data.length > 0) {
        setSelectedStatement(data[0]);
      } else {
        setSelectedStatement(null);
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch statements');
    } finally {
      setLoading(false);
    }
  };

  const fetchReconciliationSummary = async () => {
    if (!selectedStatement) return;

    try {
      const summary = await bankReconciliationService.getReconciliationSummary(selectedStatement.id);
      setReconciliationSummary(summary);
    } catch (error: any) {
      console.error('Failed to fetch reconciliation summary:', error);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const stats = await bankReconciliationService.getDashboardStats();
      setDashboardStats(stats);
    } catch (error: any) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  const handleAutoReconcile = async () => {
    if (!selectedStatement) {
      message.warning('Please select a statement first');
      return;
    }

    try {
      setLoading(true);
      const result = await bankReconciliationService.autoReconcile(selectedStatement.id);
      message.success(`Auto reconciliation completed: ${result.matchedCount} transactions matched`);
      // Refresh data
      await fetchStatements();
      await fetchReconciliationSummary();
      await fetchDashboardStats();
    } catch (error: any) {
      message.error(error.message || 'Auto reconciliation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMatchTransaction = async (lineId: string) => {
    setSelectedLineForMatch(selectedStatement?.lines?.find(l => l.id === lineId) || null);
    setMatchModalVisible(true);
    setMatchingLoading(true);

    try {
      const candidates = await bankReconciliationService.getMatchingCandidates(lineId);
      setMatchingCandidates(candidates);
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch matching candidates');
    } finally {
      setMatchingLoading(false);
    }
  };

  const handleConfirmMatch = async (journalEntryLineId: string) => {
    if (!selectedLineForMatch) return;

    try {
      await bankReconciliationService.matchTransaction(selectedLineForMatch.id, journalEntryLineId);
      message.success('Transaction matched successfully');
      setMatchModalVisible(false);
      setSelectedLineForMatch(null);
      // Refresh data
      await fetchStatements();
      await fetchReconciliationSummary();
    } catch (error: any) {
      message.error(error.message || 'Failed to match transaction');
    }
  };

  const handleUnmatchTransaction = async (lineId: string) => {
    try {
      await bankReconciliationService.unmatchTransaction(lineId);
      message.success('Transaction unmatched successfully');
      // Refresh data
      await fetchStatements();
      await fetchReconciliationSummary();
    } catch (error: any) {
      message.error(error.message || 'Failed to unmatch transaction');
    }
  };

  const handleFinalizeReconciliation = async () => {
    if (!selectedStatement) return;

    try {
      setLoading(true);
      await bankReconciliationService.finalizeReconciliation(selectedStatement.id);
      message.success('Reconciliation finalized successfully');
      await fetchStatements();
      await fetchDashboardStats();
    } catch (error: any) {
      message.error(error.message || 'Failed to finalize reconciliation');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBankAccount = async (values: any) => {
    try {
      setLoading(true);
      if (editingBankAccount) {
        await bankReconciliationService.updateBankAccount(editingBankAccount.id, values);
        message.success('Bank account updated successfully');
      } else {
        await bankReconciliationService.createBankAccount(values);
        message.success('Bank account created successfully');
      }
      setBankAccountModalVisible(false);
      setEditingBankAccount(null);
      bankAccountForm.resetFields();
      await fetchBankAccounts();
      await fetchDashboardStats();
    } catch (error: any) {
      message.error(error.message || 'Failed to save bank account');
    } finally {
      setLoading(false);
    }
  };

  const handleEditBankAccount = (account: BankAccount) => {
    setEditingBankAccount(account);
    bankAccountForm.setFieldsValue({
      accountNumber: account.accountNumber,
      accountName: account.accountName,
      bankName: account.bankName,
      branchName: account.branchName,
      swiftCode: account.swiftCode,
      journalId: (account as any).journalId,
      active: account.active
    });
    setBankAccountModalVisible(true);
  };

  const handleDeleteBankAccount = async (account: BankAccount) => {
    try {
      setLoading(true);
      await bankReconciliationService.deleteBankAccount(account.id);
      message.success('Bank account deleted successfully');
      await fetchBankAccounts();
      await fetchDashboardStats();
      // Clear selection if deleted account was selected
      if (selectedBankAccountId === account.id) {
        setSelectedBankAccountId(null);
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to delete bank account. Make sure it has no statements.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStatement = async (values: any) => {
    if (!selectedBankAccountId) {
      message.warning('Please select a bank account first');
      return;
    }

    try {
      setLoading(true);
      await bankReconciliationService.createStatement({
        bankAccountId: selectedBankAccountId,
        date: values.date.format('YYYY-MM-DD'),
        balanceStart: values.balanceStart,
        balanceEnd: values.balanceEnd
      });
      message.success('Statement created successfully');
      setStatementModalVisible(false);
      statementForm.resetFields();
      await fetchStatements();
    } catch (error: any) {
      message.error(error.message || 'Failed to create statement');
    } finally {
      setLoading(false);
    }
  };

  /** Correct a mistyped line, rather than deleting and retyping it. */
  const handleEditLine = async (values: any) => {
    if (!editingLine) return;
    try {
      setLoading(true);
      await bankReconciliationService.updateStatementLine(editingLine.id, {
        date: values.date.format('YYYY-MM-DD'),
        name: values.description,
        ref: values.reference,
        amount: values.amount
      });
      message.success('Transaction updated');
      setEditLineModalVisible(false);
      setEditingLine(null);
      editLineForm.resetFields();
      await fetchStatements();
      await fetchReconciliationSummary();
    } catch (error: any) {
      message.error(error.message || 'Failed to update transaction');
    } finally {
      setLoading(false);
    }
  };

  const openEditLine = (line: BankStatementLine) => {
    setEditingLine(line);
    editLineForm.setFieldsValue({
      date: dayjs(line.date),
      description: line.name,
      reference: line.ref,
      amount: Number(line.amount)
    });
    setEditLineModalVisible(true);
  };

  /** Correct the statement header — usually an opening or closing balance. */
  const handleEditStatement = async (values: any) => {
    if (!selectedStatement) return;
    try {
      setLoading(true);
      await bankReconciliationService.updateStatement(selectedStatement.id, {
        date: values.date.format('YYYY-MM-DD'),
        balanceStart: values.balanceStart,
        balanceEnd: values.balanceEnd
      } as any);
      message.success('Statement updated');
      setEditStatementModalVisible(false);
      await fetchStatements();
      await fetchReconciliationSummary();
    } catch (error: any) {
      message.error(error.message || 'Failed to update statement');
    } finally {
      setLoading(false);
    }
  };

  /** Undo Finalize, so a mistake found later can still be corrected. */
  const handleReopen = async () => {
    if (!selectedStatement) return;
    try {
      setLoading(true);
      await bankReconciliationService.reopenReconciliation(selectedStatement.id);
      message.success('Statement reopened');
      await fetchStatements();
      await fetchReconciliationSummary();
    } catch (error: any) {
      message.error(error.message || 'Failed to reopen statement');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLine = async (values: any) => {
    if (!selectedStatement) return;

    try {
      setLoading(true);
      await bankReconciliationService.addStatementLine(selectedStatement.id, {
        date: values.date.format('YYYY-MM-DD'),
        description: values.description,
        reference: values.reference,
        amount: values.amount
      });
      message.success('Transaction added successfully');
      setAddLineModalVisible(false);
      addLineForm.resetFields();
      await fetchStatements();
      await fetchReconciliationSummary();
    } catch (error: any) {
      message.error(error.message || 'Failed to add transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLine = async (lineId: string) => {
    try {
      await bankReconciliationService.deleteStatementLine(lineId);
      message.success('Transaction deleted successfully');
      await fetchStatements();
      await fetchReconciliationSummary();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete transaction');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const lineColumns: ColumnsType<BankStatementLine> = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date) => dayjs(date).format('YYYY-MM-DD'),
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix()
    },
    {
      title: 'Description',
      dataIndex: 'name',
      key: 'name',
      width: 250
    },
    {
      title: 'Reference',
      dataIndex: 'ref',
      key: 'ref',
      width: 120,
      render: (ref) => ref || '-'
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      align: 'right',
      render: (amount) => (
        <Text style={{ color: amount >= 0 ? '#3f8600' : '#cf1322' }}>
          {formatCurrency(amount)}
        </Text>
      ),
      sorter: (a, b) => a.amount - b.amount
    },
    {
      title: 'Status',
      dataIndex: 'isReconciled',
      key: 'isReconciled',
      width: 120,
      render: (reconciled) => (
        <Tag color={reconciled ? 'green' : 'orange'}>
          {reconciled ? 'RECONCILED' : 'UNMATCHED'}
        </Tag>
      ),
      filters: [
        { text: 'Reconciled', value: true },
        { text: 'Unmatched', value: false }
      ],
      onFilter: (value, record) => record.isReconciled === value
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          {!record.isReconciled ? (
            <>
              <Button
                type="link"
                size="small"
                icon={<LinkOutlined />}
                onClick={() => handleMatchTransaction(record.id)}
              >
                Match
              </Button>
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEditLine(record)}
              >
                Edit
              </Button>
              <Popconfirm
                title="Delete this transaction?"
                onConfirm={() => handleDeleteLine(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <Button type="text" size="small" icon={<DeleteOutlined />} danger />
              </Popconfirm>
            </>
          ) : (
            <Button
              type="link"
              size="small"
              icon={<DisconnectOutlined />}
              onClick={() => handleUnmatchTransaction(record.id)}
            >
              Unmatch
            </Button>
          )}
        </Space>
      )
    }
  ];

  const renderReconciliationTab = () => (
    <div>
      {/* Bank Account and Statement Selection */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={8}>
            <Text strong>Bank Account:</Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              value={selectedBankAccountId}
              onChange={setSelectedBankAccountId}
              placeholder="Select bank account"
              loading={loading}
            >
              {bankAccounts.map(acc => (
                <Option key={acc.id} value={acc.id}>
                  {acc.accountName} - {acc.bankName}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            <Text strong>Statement:</Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              value={selectedStatement?.id}
              onChange={(id) => setSelectedStatement(statements.find(s => s.id === id) || null)}
              placeholder="Select statement"
              disabled={!selectedBankAccountId}
            >
              {statements.map(stmt => (
                <Option key={stmt.id} value={stmt.id}>
                  {stmt.name} - {dayjs(stmt.date).format('YYYY-MM-DD')} ({stmt.state})
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            <Space style={{ marginTop: 28 }}>
              <Button
                icon={<PlusOutlined />}
                onClick={() => setStatementModalVisible(true)}
                disabled={!selectedBankAccountId}
              >
                New Statement
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => { fetchStatements(); fetchReconciliationSummary(); }}
              >
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Reconciliation Summary */}
      {selectedStatement && reconciliationSummary && (
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col span={5}>
              <Statistic
                title="Opening Balance"
                value={reconciliationSummary.balanceStart}
                prefix="$"
                precision={2}
              />
            </Col>
            <Col span={5}>
              <Statistic
                title="Closing Balance"
                value={reconciliationSummary.balanceEnd}
                prefix="$"
                precision={2}
              />
            </Col>
            <Col span={5}>
              <Statistic
                title="Reconciled"
                value={reconciliationSummary.reconciledLines}
                suffix={`/ ${reconciliationSummary.totalLines}`}
                valueStyle={{ color: '#3f8600' }}
              />
            </Col>
            <Col span={5}>
              <Statistic
                title="Unreconciled"
                value={reconciliationSummary.unreconciledLines}
                valueStyle={{ color: reconciliationSummary.unreconciledLines > 0 ? '#cf1322' : '#3f8600' }}
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="Status"
                value={selectedStatement.state}
                valueStyle={{
                  color: selectedStatement.state === 'RECONCILED' ? '#3f8600' : '#1677ff',
                  fontSize: 18
                }}
              />
            </Col>
          </Row>

          <Divider />

          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Progress
                type="circle"
                percent={reconciliationSummary.totalLines > 0
                  ? Math.round((reconciliationSummary.reconciledLines / reconciliationSummary.totalLines) * 100)
                  : 0}
                format={() => `${reconciliationSummary.reconciledLines}/${reconciliationSummary.totalLines}`}
              />
              <div style={{ textAlign: 'center', marginTop: 8 }}>
                <Text>Reconciled Transactions</Text>
              </div>
            </Col>
            <Col span={16}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  // A global rule forces .ant-btn-primary to brand blue with
                  // !important, which beats antd's disabled styling — so a
                  // disabled primary still looks clickable. The other buttons
                  // in this panel are default-variant and grey out correctly,
                  // so drop to default when disabled rather than change that
                  // global rule, which every primary button in the app uses.
                  type={selectedStatement.state === 'RECONCILED' ? 'default' : 'primary'}
                  icon={<ReconciliationOutlined />}
                  onClick={handleAutoReconcile}
                  loading={loading}
                  disabled={selectedStatement.state === 'RECONCILED'}
                  block
                >
                  Auto Reconcile
                </Button>
                <Button
                  icon={<CheckCircleOutlined />}
                  onClick={handleFinalizeReconciliation}
                  disabled={selectedStatement.state === 'RECONCILED' || reconciliationSummary.unreconciledLines > 0 || reconciliationSummary.totalLines === 0}
                  block
                >
                  Finalize Reconciliation
                </Button>
                {/* Both of these called endpoints that already existed and had
                    simply never been given a button. */}
                <Button
                  icon={<EditOutlined />}
                  onClick={() => {
                    editStatementForm.setFieldsValue({
                      date: dayjs(selectedStatement.date),
                      balanceStart: Number(selectedStatement.balanceStart),
                      balanceEnd: Number(selectedStatement.balanceEnd)
                    });
                    setEditStatementModalVisible(true);
                  }}
                  disabled={selectedStatement.state === 'RECONCILED'}
                  block
                >
                  Edit Statement
                </Button>
                {selectedStatement.state === 'RECONCILED' && (
                  <Popconfirm
                    title="Reopen this statement?"
                    description="It goes back to OPEN so you can correct it, then finalise again."
                    onConfirm={handleReopen}
                    okText="Reopen"
                    cancelText="Cancel"
                  >
                    <Button icon={<UndoOutlined />} block>Reopen Statement</Button>
                  </Popconfirm>
                )}
              </Space>
            </Col>
          </Row>
        </Card>
      )}

      {/* Transactions Table */}
      {selectedStatement ? (
        <Card
          title="Statement Transactions"
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setAddLineModalVisible(true)}
              disabled={selectedStatement.state === 'RECONCILED'}
            >
              Add Transaction
            </Button>
          }
        >
          <Table
            columns={lineColumns}
            dataSource={selectedStatement.lines || []}
            rowKey="id"
            loading={loading}
            pagination={{ defaultPageSize: 20, showSizeChanger: true }}
            size="small"
            bordered
            summary={(pageData) => {
              const totalAmount = pageData.reduce((sum, record) => sum + Number(record.amount), 0);
              const reconciledAmount = pageData.filter(r => r.isReconciled).reduce((sum, r) => sum + Number(r.amount), 0);
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3}>
                      <strong>Totals</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <strong>{formatCurrency(totalAmount)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} colSpan={2}>
                      <Text type="secondary">Reconciled: {formatCurrency(reconciledAmount)}</Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </Card>
      ) : (
        <Card>
          <Empty
            image={<BankOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
            description="Select a bank account and statement to view transactions"
          />
        </Card>
      )}

      {/* Bank Account Modal */}
      <Modal
        title={editingBankAccount ? 'Edit Bank Account' : 'New Bank Account'}
        open={bankAccountModalVisible}
        onCancel={() => {
          setBankAccountModalVisible(false);
          setEditingBankAccount(null);
          bankAccountForm.resetFields();
        }}
        onOk={() => bankAccountForm.submit()}
        confirmLoading={loading}
      >
        <Form form={bankAccountForm} layout="vertical" onFinish={handleCreateBankAccount}>
          <Form.Item name="accountNumber" label="Account Number" rules={[{ required: true }]}>
            <Input placeholder="Enter account number" />
          </Form.Item>
          <Form.Item name="accountName" label="Account Name" rules={[{ required: true }]}>
            <Input placeholder="Enter account name" />
          </Form.Item>
          <Form.Item name="bankName" label="Bank Name" rules={[{ required: true }]}>
            <Input placeholder="Enter bank name" />
          </Form.Item>
          <Form.Item name="branchName" label="Branch Name">
            <Input placeholder="Enter branch name" />
          </Form.Item>
          <Form.Item name="swiftCode" label="SWIFT Code">
            <Input placeholder="Enter SWIFT code" />
          </Form.Item>
          <Form.Item
            name="journalId"
            label="Journal"
            rules={[{ required: true, message: 'Choose the journal this account posts to' }]}
            extra="Reconciliation matches statement lines against entries in this journal. Without it, no matches can be found."
          >
            <Select
              placeholder="Select the journal this bank account posts to"
              showSearch
              optionFilterProp="label"
              options={journals.map(j => ({
                value: j.id,
                label: `${j.code} - ${j.name}${j.type ? ` (${j.type})` : ''}`
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Statement Modal */}
      <Modal
        title="New Bank Statement"
        open={statementModalVisible}
        onCancel={() => setStatementModalVisible(false)}
        onOk={() => statementForm.submit()}
        confirmLoading={loading}
      >
        <Form form={statementForm} layout="vertical" onFinish={handleCreateStatement}>
          <Form.Item name="date" label="Statement Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="balanceStart" label="Opening Balance" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} prefix="$" precision={2} />
          </Form.Item>
          <Form.Item name="balanceEnd" label="Closing Balance" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} prefix="$" precision={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Line Modal */}
      <Modal
        title="Add Transaction"
        open={addLineModalVisible}
        onCancel={() => setAddLineModalVisible(false)}
        onOk={() => addLineForm.submit()}
        confirmLoading={loading}
      >
        <Form form={addLineForm} layout="vertical" onFinish={handleAddLine}>
          <Form.Item name="date" label="Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{ required: true }]}>
            <Input placeholder="Enter transaction description" />
          </Form.Item>
          <Form.Item name="reference" label="Reference">
            <Input placeholder="Enter reference number" />
          </Form.Item>
          <Form.Item name="amount" label="Amount" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} prefix="$" precision={2} placeholder="Positive for credit, negative for debit" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Line Modal */}
      <Modal
        title="Edit Transaction"
        open={editLineModalVisible}
        onCancel={() => { setEditLineModalVisible(false); setEditingLine(null); }}
        onOk={() => editLineForm.submit()}
        confirmLoading={loading}
      >
        <Form form={editLineForm} layout="vertical" onFinish={handleEditLine}>
          <Form.Item name="date" label="Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{ required: true }]}>
            <Input placeholder="Enter transaction description" />
          </Form.Item>
          <Form.Item name="reference" label="Reference">
            <Input placeholder="Enter reference number" />
          </Form.Item>
          <Form.Item name="amount" label="Amount" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} prefix="$" precision={2}
              placeholder="Positive for money in, negative for money out" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Statement Modal */}
      <Modal
        title="Edit Statement"
        open={editStatementModalVisible}
        onCancel={() => setEditStatementModalVisible(false)}
        onOk={() => editStatementForm.submit()}
        confirmLoading={loading}
      >
        <Form form={editStatementForm} layout="vertical" onFinish={handleEditStatement}>
          <Form.Item name="date" label="Statement Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="balanceStart" label="Opening Balance" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} prefix="$" precision={2} />
          </Form.Item>
          <Form.Item name="balanceEnd" label="Closing Balance" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} prefix="$" precision={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Match Modal */}
      <Modal
        title="Match Transaction"
        open={matchModalVisible}
        onCancel={() => { setMatchModalVisible(false); setSelectedLineForMatch(null); }}
        footer={null}
        width={800}
      >
        {selectedLineForMatch && (
          <div>
            <Card size="small" style={{ marginBottom: 16 }}>
              <Text strong>Transaction to Match:</Text>
              <br />
              <Text>{selectedLineForMatch.name}</Text>
              <br />
              <Text type="secondary">Amount: {formatCurrency(selectedLineForMatch.amount)}</Text>
              {selectedLineForMatch.ref && (
                <>
                  <br />
                  <Text type="secondary">Ref: {selectedLineForMatch.ref}</Text>
                </>
              )}
            </Card>

            <Text strong>Matching Candidates:</Text>
            {matchingLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin />
              </div>
            ) : matchingCandidates.length > 0 ? (
              <Table
                dataSource={matchingCandidates}
                rowKey="id"
                size="small"
                pagination={false}
                columns={[
                  {
                    title: 'Entry',
                    dataIndex: ['entry', 'name'],
                    key: 'entry'
                  },
                  {
                    title: 'Date',
                    dataIndex: ['entry', 'date'],
                    key: 'date',
                    render: (date) => dayjs(date).format('YYYY-MM-DD')
                  },
                  {
                    title: 'Ref',
                    dataIndex: ['entry', 'ref'],
                    key: 'ref',
                    render: (ref) => ref || '-'
                  },
                  {
                    // The entry already carried the invoice number; the dialog
                    // just never showed it, so two candidates of the same
                    // amount looked identical. (A Description column was tried
                    // here too, but these entries keep their description in
                    // `ref`, shown above — `narration` is empty on all 8,932
                    // posted bank entries, so the column only ever read "-".)
                    title: 'Invoice No',
                    dataIndex: ['entry', 'invoiceNo'],
                    key: 'invoiceNo',
                    render: (invoiceNo) => invoiceNo || '-'
                  },
                  {
                    title: 'Amount',
                    key: 'amount',
                    render: (_, record) => formatCurrency(record.debit || record.credit)
                  },
                  {
                    title: 'Action',
                    key: 'action',
                    render: (_, record) => (
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => handleConfirmMatch(record.id)}
                      >
                        Match
                      </Button>
                    )
                  }
                ]}
              />
            ) : (
              <Empty description="No matching candidates found" />
            )}
          </div>
        )}
      </Modal>
    </div>
  );

  const renderHistoryTab = () => (
    <Card title="Reconciliation History">
      <Table
        columns={[
          {
            title: 'Statement',
            dataIndex: 'name',
            key: 'name'
          },
          {
            title: 'Bank Account',
            dataIndex: ['bankAccount', 'accountName'],
            key: 'bankAccount'
          },
          {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (date) => dayjs(date).format('YYYY-MM-DD')
          },
          {
            title: 'Balance Start',
            dataIndex: 'balanceStart',
            key: 'balanceStart',
            render: (val) => formatCurrency(Number(val))
          },
          {
            title: 'Balance End',
            dataIndex: 'balanceEnd',
            key: 'balanceEnd',
            render: (val) => formatCurrency(Number(val))
          },
          {
            title: 'Status',
            dataIndex: 'state',
            key: 'state',
            render: (state) => (
              <Tag color={state === 'RECONCILED' ? 'green' : 'blue'}>
                {state}
              </Tag>
            )
          },
          {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
              <Button
                type="link"
                onClick={() => {
                  setSelectedStatement(record);
                  setActiveTab('reconciliation');
                }}
              >
                View
              </Button>
            )
          }
        ]}
        dataSource={statements.filter(s => s.state === 'RECONCILED')}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />
    </Card>
  );

  const renderSettingsTab = () => (
    <Card
      title="Bank Accounts Management"
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingBankAccount(null);
            bankAccountForm.resetFields();
            setBankAccountModalVisible(true);
          }}
        >
          New Account
        </Button>
      }
    >
      <Table
        dataSource={bankAccounts}
        rowKey="id"
        columns={[
          { title: 'Account Name', dataIndex: 'accountName', key: 'accountName' },
          { title: 'Account Number', dataIndex: 'accountNumber', key: 'accountNumber' },
          { title: 'Bank Name', dataIndex: 'bankName', key: 'bankName' },
          { title: 'Branch', dataIndex: 'branchName', key: 'branchName', render: (v) => v || '-' },
          {
            title: 'Status',
            dataIndex: 'active',
            key: 'active',
            render: (active) => (
              <Tag color={active ? 'green' : 'red'}>
                {active ? 'Active' : 'Inactive'}
              </Tag>
            )
          },
          {
            // Surfaced because a missing journal silently breaks matching, and
            // there was no way to see it from this screen.
            title: 'Journal',
            key: 'journalId',
            render: (_: any, record: BankAccount) => {
              const j = journals.find(x => x.id === (record as any).journalId);
              if (j) return <span>{j.code} - {j.name}</span>;
              return <Tag color="warning">Not set — matching will find nothing</Tag>;
            }
          },
          {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: BankAccount) => (
              <Space>
                <Button type="link" size="small" onClick={() => handleEditBankAccount(record)}>Edit</Button>
                <Popconfirm
                  title="Delete Bank Account"
                  description="Are you sure you want to delete this bank account? This cannot be undone."
                  onConfirm={() => handleDeleteBankAccount(record)}
                  okText="Yes, Delete"
                  cancelText="Cancel"
                  okButtonProps={{ danger: true }}
                >
                  <Button type="link" size="small" danger>Delete</Button>
                </Popconfirm>
              </Space>
            )
          }
        ]}
        pagination={false}
      />

      <Divider />

      {dashboardStats && (
        <Row gutter={16}>
          <Col span={6}>
            <Statistic title="Total Bank Accounts" value={dashboardStats.totalBankAccounts} />
          </Col>
          <Col span={6}>
            <Statistic title="Active Accounts" value={dashboardStats.activeBankAccounts} />
          </Col>
          <Col span={6}>
            <Statistic title="Open Statements" value={dashboardStats.openStatements} />
          </Col>
          <Col span={6}>
            <Statistic title="Pending Transactions" value={dashboardStats.pendingTransactions} />
          </Col>
        </Row>
      )}
    </Card>
  );

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <BankOutlined style={{ marginRight: 12 }} />
          Bank Reconciliation
        </Title>
        <Text type="secondary">
          Reconcile bank statements with accounting records
        </Text>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: 'reconciliation', label: 'Reconciliation', children: renderReconciliationTab() },
          { key: 'history', label: 'History', children: renderHistoryTab() },
          { key: 'settings', label: 'Bank Accounts', children: renderSettingsTab() }
        ]}
      />
    </div>
  );
};

export default BankReconciliation;